import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Box, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import RuleIcon from '@mui/icons-material/Rule';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import HealingIcon from '@mui/icons-material/Healing';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import BugReportIcon from '@mui/icons-material/BugReport';
import BarChartIcon from '@mui/icons-material/BarChart';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

function Sidebar({ activeTab, isAdmin, isCollapsed = false, onToggleCollapse }) {
  const mainNavItems = [
    { id: 'anomalies', label: 'Anomalies', icon: <BugReportIcon />, path: '/anomalies' },
    { id: 'metrics', label: 'Metrics', icon: <BarChartIcon />, path: '/metrics' },
    { id: 'rules', label: 'Rules', icon: <RuleIcon />, path: '/rules' },
    { id: 'rule-synthesis', label: 'Rule Synthesis', icon: <CalendarTodayIcon />, path: '/rule-synthesis' },
    { id: 'jobs', label: 'Job Tracker', icon: <HealingIcon />, path: '/jobs' },
    { id: 'skills', label: 'Skills', icon: <AutoFixHighIcon />, path: '/skills' },
    { id: 'settings', label: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  ];

  const adminNavItems = isAdmin ? [
    { id: 'admin', label: 'Admin Console', icon: <AdminPanelSettingsIcon />, path: '/admin' },
  ] : [];

  return (
    <Box
      sx={{
        width: isCollapsed ? 70 : 240,
        height: '100vh',
        background: '#1e293b',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        left: 0,
        top: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        boxShadow: '4px 0 12px rgba(0, 0, 0, 0.3)',
        zIndex: 1200,
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        willChange: 'width',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '3px',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.3)',
          },
        },
      }}
    >
      {/* Logo/Brand Section */}
      <Box 
        sx={{ 
          p: 2,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          minHeight: 64,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
          <img
            src="/logo-ciq.png"
            alt="CommerceIQ"
            style={{ width: 36, height: 36, flexShrink: 0, borderRadius: 8 }}
          />
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700,
              fontSize: '1.1rem',
              letterSpacing: '-0.5px',
              whiteSpace: 'nowrap',
              opacity: isCollapsed ? 0 : 1,
              transition: 'opacity 0.2s ease',
            }}
          >
            CommerceIQ
          </Typography>
        </Box>
      </Box>

      {/* Toggle Button */}
      <Box sx={{ px: 2, mb: 2, transition: 'padding 0.3s ease' }}>
        <IconButton
          onClick={onToggleCollapse}
          sx={{
            color: '#94a3b8',
            width: '100%',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              color: '#e2e8f0'
            }
          }}
        >
          {isCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mx: 2, mb: 2 }} />

      {/* Main Navigation */}
      <List sx={{ px: 1, flex: 1 }}>
        {mainNavItems.map((item) => (
          <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
            <Tooltip title={isCollapsed ? item.label : ''} placement="right" arrow>
              <ListItemButton
                component={Link}
                to={item.path}
                selected={activeTab === item.id}
                sx={{
                  borderRadius: '8px',
                  py: 1.5,
                  textDecoration: 'none',
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  overflow: 'hidden',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(59, 130, 246, 0.15)',
                    borderLeft: '3px solid #3b82f6',
                    '&:hover': {
                      backgroundColor: 'rgba(59, 130, 246, 0.2)',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ 
                  color: activeTab === item.id ? '#3b82f6' : '#94a3b8',
                  minWidth: 40,
                  justifyContent: 'center',
                }}>
                  {item.icon}
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: activeTab === item.id ? 600 : 500,
                      color: activeTab === item.id ? '#e2e8f0' : '#cbd5e1',
                      whiteSpace: 'nowrap',
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}

        {/* Admin Navigation */}
        {adminNavItems.length > 0 && (
          <>
            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 2 }} />
            {adminNavItems.map((item) => (
              <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
                <Tooltip title={isCollapsed ? item.label : ''} placement="right" arrow>
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    selected={activeTab === item.id}
                    sx={{
                      borderRadius: '8px',
                      py: 1.5,
                      textDecoration: 'none',
                      justifyContent: isCollapsed ? 'center' : 'flex-start',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(245, 158, 11, 0.15)',
                        borderLeft: '3px solid #f59e0b',
                        '&:hover': {
                          backgroundColor: 'rgba(245, 158, 11, 0.2)',
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: activeTab === item.id ? '#f59e0b' : '#94a3b8',
                      minWidth: 40,
                      justifyContent: 'center',
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    {!isCollapsed && (
                      <ListItemText 
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.9rem',
                          fontWeight: activeTab === item.id ? 600 : 500,
                          color: activeTab === item.id ? '#e2e8f0' : '#cbd5e1',
                          whiteSpace: 'nowrap',
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </>
        )}
      </List>
    </Box>
  );
}

export default Sidebar;

